import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Edit3, Trash2, BarChart3, Smile, Meh, Frown, TrendingUp, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PesquisaTab() {
  const { user } = useAuth();
  const podeGerenciar = ["diretor", "gerente"].includes(user?.role);
  const podeVerRelatorio = ["diretor", "gerente", "supervisao"].includes(user?.role);

  return (
    <Tabs defaultValue="perguntas" className="space-y-4">
      <TabsList data-testid="pesquisa-subtabs">
        <TabsTrigger value="perguntas" data-testid="subtab-perguntas">Perguntas</TabsTrigger>
        {podeVerRelatorio && (
          <TabsTrigger value="relatorio" data-testid="subtab-relatorio">Relatório NPS</TabsTrigger>
        )}
      </TabsList>

      <TabsContent value="perguntas" className="m-0">
        <PerguntasGestao podeGerenciar={podeGerenciar} />
      </TabsContent>

      {podeVerRelatorio && (
        <TabsContent value="relatorio" className="m-0">
          <RelatorioNPS />
        </TabsContent>
      )}
    </Tabs>
  );
}

// ────────────────────────────────────────────────────────────
function PerguntasGestao({ podeGerenciar }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, edit: null });

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pesquisa/perguntas", { params: { incluir_inativas: true } });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Falha ao carregar perguntas");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const toggle = async (p) => {
    try {
      await api.patch(`/pesquisa/perguntas/${p.id}/toggle`);
      toast.success(p.ativa ? "Pergunta desativada" : "Pergunta ativada");
      carregar();
    } catch (e) { toast.error("Falha"); }
  };

  const excluir = async (p) => {
    if (!window.confirm(`Excluir a pergunta "${p.texto}"?`)) return;
    try {
      await api.delete(`/pesquisa/perguntas/${p.id}`);
      toast.success("Pergunta excluída");
      carregar();
    } catch (e) { toast.error("Falha ao excluir"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-600">
          Perguntas exibidas para o cliente após o ticket ser concluído. Escala 0-10 (NPS).
        </p>
        {podeGerenciar && (
          <Button
            size="sm" className="bg-orange-600 hover:bg-orange-700"
            onClick={() => setModal({ open: true, edit: null })}
            data-testid="btn-nova-pergunta"
          >
            <Plus size={14} className="mr-1" /> Adicionar pergunta
          </Button>
        )}
      </div>

      <Card data-testid="tabela-perguntas">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">#</TableHead>
              <TableHead>PERGUNTA</TableHead>
              <TableHead className="w-24">STATUS</TableHead>
              <TableHead className="text-right w-44">AÇÕES</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={4} className="text-center py-6">
                <Loader2 className="animate-spin inline" />
              </TableCell></TableRow>
            )}
            {!loading && items.map((p) => (
              <TableRow key={p.id} data-testid={`row-pergunta-${p.id}`}>
                <TableCell className="font-mono text-xs">{p.ordem}</TableCell>
                <TableCell className="text-xs">{p.texto}</TableCell>
                <TableCell>
                  <Badge className={p.ativa ? "bg-emerald-100 text-emerald-800 border border-emerald-300" : "bg-slate-100 text-slate-600 border border-slate-300"}>
                    {p.ativa ? "ATIVA" : "INATIVA"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {podeGerenciar && (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setModal({ open: true, edit: p })} data-testid={`editar-pergunta-${p.id}`}>
                        <Edit3 size={12} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggle(p)} data-testid={`toggle-pergunta-${p.id}`}>
                        {p.ativa ? "Desativar" : "Ativar"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => excluir(p)} data-testid={`del-pergunta-${p.id}`} className="text-red-600 hover:bg-red-50">
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <ModalPergunta
        open={modal.open}
        edit={modal.edit}
        onOpenChange={(v) => !v && setModal({ open: false, edit: null })}
        onSaved={carregar}
      />
    </div>
  );
}

function ModalPergunta({ open, edit, onOpenChange, onSaved }) {
  const [texto, setTexto] = useState("");
  const [ativa, setAtiva] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) {
      setTexto(edit?.texto || "");
      setAtiva(edit?.ativa ?? true);
      setSalvando(false);
    }
  }, [open, edit]);

  const submit = async () => {
    if (!texto.trim() || texto.length < 5) {
      toast.error("Digite uma pergunta válida (>= 5 caracteres)"); return;
    }
    setSalvando(true);
    try {
      const body = { texto: texto.trim(), ativa };
      if (edit) {
        await api.put(`/pesquisa/perguntas/${edit.id}`, body);
        toast.success("Pergunta atualizada");
      } else {
        await api.post("/pesquisa/perguntas", body);
        toast.success("Pergunta criada");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    } finally { setSalvando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="modal-pergunta">
        <DialogHeader>
          <DialogTitle>{edit ? "Editar pergunta" : "Nova pergunta"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase">Pergunta *</Label>
            <Input
              value={texto} onChange={(e) => setTexto(e.target.value)}
              placeholder="Ex: Como você avalia nosso atendimento?"
              maxLength={300}
              data-testid="pergunta-texto"
            />
            <p className="text-[10px] text-slate-500 mt-1">{texto.length}/300 caracteres</p>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={ativa} onCheckedChange={setAtiva} data-testid="pergunta-ativa" />
            <Label>Ativa (exibida para clientes)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={salvando}>Cancelar</Button>
          <Button onClick={submit} disabled={salvando} className="bg-orange-600 hover:bg-orange-700" data-testid="pergunta-salvar">
            {salvando && <Loader2 size={12} className="animate-spin mr-2" />}
            {edit ? "Salvar" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────
function RelatorioNPS() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [desde, setDesde] = useState("");
  const [ate, setAte] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (ate) params.ate = ate;
      const { data: d } = await api.get("/pesquisa/relatorio", { params });
      setData(d);
    } catch (e) { toast.error("Falha ao carregar relatório"); }
    finally { setLoading(false); }
  }, [desde, ate]);

  useEffect(() => { carregar(); }, [carregar]);

  if (loading) {
    return <Card className="p-8 text-center"><Loader2 className="animate-spin inline" /></Card>;
  }
  if (!data) return null;

  const corNPS = data.nps_geral >= 50 ? "emerald" : data.nps_geral >= 0 ? "amber" : "red";

  return (
    <div className="space-y-4" data-testid="relatorio-nps">
      {/* Filtros */}
      <div className="flex items-end gap-2">
        <div>
          <Label className="text-xs uppercase">De</Label>
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9 w-40" data-testid="filtro-desde" />
        </div>
        <div>
          <Label className="text-xs uppercase">Até</Label>
          <Input type="date" value={ate} onChange={(e) => setAte(e.target.value)} className="h-9 w-40" data-testid="filtro-ate" />
        </div>
        <Button size="sm" variant="outline" onClick={() => { setDesde(""); setAte(""); }}>Limpar</Button>
      </div>

      {/* KPIs principais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className={`p-4 border-l-4 border-${corNPS}-500`} data-testid="kpi-nps">
          <div className="text-xs uppercase text-slate-500">NPS Geral</div>
          <div className={`text-4xl font-bold mt-1 text-${corNPS}-700`}>{data.nps_geral.toFixed(0)}</div>
          <div className="text-[10px] text-slate-500 mt-1">
            {data.nps_geral >= 50 ? "Excelente" : data.nps_geral >= 0 ? "Razoável" : "Crítico"}
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-blue-500">
          <div className="text-xs uppercase text-slate-500">Média geral</div>
          <div className="text-4xl font-bold mt-1 text-blue-700">{data.media_geral.toFixed(1)}</div>
          <div className="text-[10px] text-slate-500 mt-1">Escala 0-10</div>
        </Card>
        <Card className="p-4 border-l-4 border-orange-500">
          <div className="text-xs uppercase text-slate-500">Taxa de resposta</div>
          <div className="text-4xl font-bold mt-1 text-orange-700">{data.taxa_resposta.toFixed(0)}%</div>
          <div className="text-[10px] text-slate-500 mt-1">{data.pesquisas_respondidas}/{data.pesquisas_disparadas} pesquisas</div>
        </Card>
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase text-slate-500">Respostas</div>
          <div className="text-4xl font-bold mt-1 text-purple-700">{data.total_respostas}</div>
          <div className="text-[10px] text-slate-500 mt-1">no período</div>
        </Card>
      </div>

      {/* Distribuição */}
      <Card className="p-4">
        <h4 className="text-xs uppercase text-slate-500 font-semibold mb-3">Distribuição (todas as respostas)</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-emerald-50 rounded border border-emerald-200" data-testid="promotores">
            <Smile className="inline text-emerald-600" size={20} />
            <div className="text-2xl font-bold text-emerald-700 mt-1">{data.promotores}</div>
            <div className="text-xs text-emerald-800 font-medium">Promotores (9-10)</div>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded border border-amber-200" data-testid="neutros">
            <Meh className="inline text-amber-600" size={20} />
            <div className="text-2xl font-bold text-amber-700 mt-1">{data.neutros}</div>
            <div className="text-xs text-amber-800 font-medium">Neutros (7-8)</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded border border-red-200" data-testid="detratores">
            <Frown className="inline text-red-600" size={20} />
            <div className="text-2xl font-bold text-red-700 mt-1">{data.detratores}</div>
            <div className="text-xs text-red-800 font-medium">Detratores (0-6)</div>
          </div>
        </div>
      </Card>

      {/* Por pergunta */}
      <Card data-testid="tabela-por-pergunta">
        <div className="p-3 border-b">
          <h4 className="text-xs uppercase text-slate-500 font-semibold">NPS por pergunta</h4>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PERGUNTA</TableHead>
              <TableHead className="w-16 text-right">N</TableHead>
              <TableHead className="w-20 text-right">MÉDIA</TableHead>
              <TableHead className="w-20 text-right">NPS</TableHead>
              <TableHead className="w-32">DISTRIBUIÇÃO</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.por_pergunta.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">
                Sem respostas registradas no período.
              </TableCell></TableRow>
            )}
            {data.por_pergunta.map((p) => (
              <TableRow key={p.pergunta_id}>
                <TableCell className="text-xs">{p.pergunta_texto}</TableCell>
                <TableCell className="text-right text-xs font-mono">{p.count}</TableCell>
                <TableCell className="text-right text-xs font-mono">{p.media.toFixed(1)}</TableCell>
                <TableCell className={`text-right text-xs font-mono font-bold ${p.nps >= 50 ? "text-emerald-700" : p.nps >= 0 ? "text-amber-700" : "text-red-700"}`}>
                  {p.nps.toFixed(0)}
                </TableCell>
                <TableCell className="text-[10px]">
                  <span className="text-emerald-700">P:{p.promotores}</span>{" "}
                  <span className="text-amber-700">N:{p.neutros}</span>{" "}
                  <span className="text-red-700">D:{p.detratores}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
